import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch, readApiJson, okOrThrow } from '../lib/api';

let _cache = null;
let _globalDefaults = null;
let _listeners = new Set();
// Génération du cache : une lecture partie AVANT un vidage (déconnexion pendant le chargement)
// ne doit pas remplir le cache du compte suivant avec les préférences du précédent.
let _generation = 0;
// Vrai seulement après une lecture RÉUSSIE du serveur pour le compte courant (génération en cours).
// Source unique de `prefsChargees` : un cache peut exister sans elle (écriture locale après une
// lecture en échec — glisser un menu appelle updatePrefs) et ne dit alors rien de ce que le compte
// a déjà vu ou refusé (05/10/2026).
let _luDuServeur = false;
// Visites écrites AVANT la première lecture (cache encore null) : déjà envoyées au serveur, gardées
// ici pour être fusionnées dans la lecture quand elle revient. Jamais de cache partiel (il
// passerait pour « chargé » et bloquerait la lecture des autres préférences).
let _visitesEnAttente = {};
// Lecture en cours, PARTAGÉE : les consommateurs montés ensemble (Sidebar + page + contrôleur de
// visite, même commit) voyaient tous `_cache === null` et lançaient chacun la leur ; la première
// fusionnait l'attente et la vidait, les suivantes écrasaient le cache sans elle.
let _chargement = null;

function notify() { _listeners.forEach(fn => fn({ ..._cache })); }

function chargerPreferences() {
  if (_chargement) return _chargement;
  const generation = _generation;
  const promesse = Promise.all([
    apiFetch('/api/user/preferences').then(readApiJson).catch(() => ({ ok: false })),
    apiFetch('/api/admin/sidebar-defaults').then(readApiJson).catch(() => ({ ok: false })),
  ]).then(([userRes, defaultsRes]) => {
    // Cache vidé pendant la lecture (déconnexion) : ces préférences sont celles du compte
    // précédent — on les jette.
    if (generation !== _generation) return;
    // Lecture en échec (5xx, réseau, réponse illisible) : RIEN n'est chargé. Un `{}` en cache
    // passait pour « chargé » — l'invitation « Premiers pas » reparaissait chez qui l'avait refusée
    // ou terminée, et « Plus tard » aurait écrasé refuse/termine. Le cache reste vide, les visites
    // en attente aussi : le prochain consommateur qui monte relance la lecture (05/10/2026).
    if (!userRes.ok) {
      console.warn('[préférences] lecture impossible : préférences non chargées, nouvel essai au prochain écran');
      return;
    }
    // Écrit ici AVANT cette première lecture réussie (cache partiel né d'une écriture après une
    // lecture en échec) : déjà envoyé au serveur, plus récent que ce qu'il vient de rendre — gardé,
    // visites fusionnées clé par clé (les autres visites du serveur restent).
    const ecritAvant = (!_luDuServeur && _cache) || {};
    const { visites: visitesEcrites, ...autresEcrites } = ecritAvant;
    const userPrefs = { ...(userRes.preferences || {}), ...autresEcrites };
    // Visites écrites pendant la lecture : plus récentes que ce que le serveur vient de rendre.
    if (visitesEcrites || Object.keys(_visitesEnAttente).length) {
      userPrefs.visites = { ...(userPrefs.visites || {}), ...(visitesEcrites || {}), ..._visitesEnAttente };
      _visitesEnAttente = {};
    }
    _globalDefaults = defaultsRes.ok ? (defaultsRes.defaults || null) : null;

    // Apply global defaults as fallback for sidebar prefs
    if (_globalDefaults) {
      if (!userPrefs.sidebar_order && _globalDefaults.sidebar_order) {
        userPrefs.sidebar_order = _globalDefaults.sidebar_order;
      }
      if (!userPrefs.sidebar_items && _globalDefaults.sidebar_items) {
        userPrefs.sidebar_items = _globalDefaults.sidebar_items;
      }
    }

    _cache = userPrefs;
    _luDuServeur = true;
    notify();
  }).finally(() => {
    // Seulement si c'est toujours la lecture en cours (un vidage a pu en lancer une autre).
    if (_chargement === promesse) _chargement = null;
  });
  _chargement = promesse;
  return promesse;
}

/**
 * Vide le cache des préférences (déconnexion, changement de compte dans le même onglet) : le
 * compte suivant n'hérite jamais des préférences du précédent — dont l'état de ses visites
 * guidées (spec visite guidée §4.4, 05/10/2026).
 * Pas de rechargement ici : la déconnexion mène hors du BackofficeLayout, tous les consommateurs
 * se démontent ; au remontage, la garde `if (!_luDuServeur)` relit les préférences du NOUVEAU compte.
 */
export function resetPreferencesCache() {
  _cache = null;
  _luDuServeur = false;
  _globalDefaults = null;
  _visitesEnAttente = {};
  _chargement = null;   // la lecture en cours (ancien compte) sera jetée par la génération
  _generation += 1;
  // L'échelle d'affichage est posée sur <html>, hors du cache : un compte sans `ui_scale`
  // garderait sinon le zoom du précédent (index.css rend 1 une fois la valeur inline retirée).
  if (typeof document !== 'undefined') document.documentElement.style.removeProperty('--ui-scale');
  _listeners.forEach(fn => fn(null));
}

/**
 * usePreferences — loads and persists user preferences (sidebar order, column orders, etc.)
 * Shared across all components via in-memory cache + listeners.
 * Falls back to global sidebar defaults (set by super admin) when user has no custom prefs.
 */
export function usePreferences() {
  const [prefs, setPrefs] = useState(_cache || {});
  // Vrai seulement une fois la lecture du SERVEUR réussie pour ce compte (`_luDuServeur`), jamais
  // sur une simple écriture locale : avant, `prefs` ne dit rien de ce que le compte a déjà vu
  // (la visite guidée attend ce signal pour se proposer).
  const [prefsChargees, setPrefsChargees] = useState(_luDuServeur);
  // Cache vu au PREMIER rendu (celui de l'état initial) : l'effet compare pour savoir s'il a changé
  // entre-temps.
  const initial = useRef(_cache);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    // `null` = cache vidé (resetPreferencesCache) : plus rien de chargé pour ce compte.
    const listener = (p) => {
      if (!mounted.current) return;
      setPrefs(p || {});
      setPrefsChargees(_luDuServeur);
    };
    _listeners.add(listener);
    // La lecture partagée est revenue ENTRE le rendu et cet effet (commit lent) : son notify() est
    // passé avant que l'écouteur soit posé — sans rattrapage, ce consommateur restait à {} et
    // `prefsChargees` à faux (le contrôleur de visite ne se proposait jamais). Rattrapage seulement
    // dans ce cas : cache déjà là au rendu = aucun rendu de plus.
    if (_cache && _cache !== initial.current) listener({ ..._cache });

    // Lecture tant que le serveur n'a pas répondu pour ce compte — même si une écriture locale a
    // déjà créé un cache (une seule lecture partagée par tous les consommateurs).
    if (!_luDuServeur) chargerPreferences();

    return () => { mounted.current = false; _listeners.delete(listener); };
  }, []);

  const updatePrefs = useCallback(async (partial) => {
    _cache = { ..._cache, ...partial };
    notify();
    // Pas de toast ici (appelé en rafale par le glisser-déposer, l'échelle, les colonnes), mais jamais de refus muet :
    // le cache dit « enregistré » alors que le serveur a refusé, le réglage reviendrait au prochain chargement.
    try {
      await apiFetch('/api/user/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(partial),
      }).then(okOrThrow);
    } catch (e) {
      console.warn(`[préférences] réglage non enregistré (${(e && e.message) || 'erreur réseau'})`);
    }
  }, []);

  const setSidebarOrder = useCallback((order) => {
    updatePrefs({ sidebar_order: order });
  }, [updatePrefs]);

  const setColumnOrder = useCallback((tableId, columns) => {
    const current = _cache?.column_orders || {};
    updatePrefs({ column_orders: { ...current, [tableId]: columns } });
  }, [updatePrefs]);

  const setSidebarItems = useCallback((items) => {
    updatePrefs({ sidebar_items: items });
  }, [updatePrefs]);

  const setCustomLabel = useCallback((key, label) => {
    const current = _cache?.custom_labels || {};
    if (label) {
      updatePrefs({ custom_labels: { ...current, [key]: label } });
    } else {
      const next = { ...current };
      delete next[key];
      updatePrefs({ custom_labels: next });
    }
  }, [updatePrefs]);

  // Save current sidebar config as global default (super admin only)
  const saveAsGlobalDefault = useCallback(async () => {
    const payload = {
      sidebar_order: _cache?.sidebar_order || null,
      sidebar_items: _cache?.sidebar_items || null,
      custom_labels: _cache?.custom_labels || null,
    };
    const res = await apiFetch('/api/admin/sidebar-defaults', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(readApiJson);
    if (res.ok) _globalDefaults = payload;
    return res;
  }, []);

  const setUiScale = useCallback((scale) => {
    updatePrefs({ ui_scale: scale });
    document.documentElement.style.setProperty('--ui-scale', scale);
  }, [updatePrefs]);

  const setSidebarAlphaSort = useCallback((enabled) => {
    // Legacy single-flag setter — retained for backward compat. Sets the
    // umbrella flag; new fine-grained flags (sections/items) inherit from
    // it via ?? in the getters below until they are explicitly set.
    updatePrefs({ sidebar_alpha_sort: !!enabled });
  }, [updatePrefs]);

  const setSidebarAlphaSortSections = useCallback((enabled) => {
    updatePrefs({ sidebar_alpha_sort_sections: !!enabled });
  }, [updatePrefs]);

  const setSidebarAlphaSortItems = useCallback((enabled) => {
    updatePrefs({ sidebar_alpha_sort_items: !!enabled });
  }, [updatePrefs]);

  // Visites guidées : n'envoie QUE la visite touchée — le serveur l'écrit seule (`$set
  // visites.<id>`), deux onglets ne s'écrasent pas. `maj` = horodatage de ce poste.
  const setVisite = useCallback((id, etat) => {
    const entree = { etat: etat.etat, etape: etat.etape ?? null, maj: new Date().toISOString() };
    if (_cache) {
      _cache = { ..._cache, visites: { ...(_cache.visites || {}), [id]: entree } };
      notify();
    } else {
      // Préférences pas encore lues : la lecture en cours fusionnera cette entrée.
      _visitesEnAttente = { ..._visitesEnAttente, [id]: entree };
    }
    // Pas de toast (la visite ne doit pas gêner), mais jamais de refus muet : le cache dit
    // « terminé / refusé » alors que rien n'est enregistré, la visite reviendrait au prochain onglet.
    apiFetch('/api/user/preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visites: { [id]: entree } }),
    }).then((res) => {
      if (!res || !res.ok) {
        console.warn(`[préférences] visite « ${id} » non enregistrée (HTTP ${res ? res.status : '?'})`);
      }
    }).catch((e) => {
      console.warn(`[préférences] visite « ${id} » non enregistrée (${(e && e.message) || 'erreur réseau'})`);
    });
  }, []);

  // Apply ui_scale on load
  useEffect(() => {
    if (prefs.ui_scale) {
      document.documentElement.style.setProperty('--ui-scale', prefs.ui_scale);
    }
  }, [prefs.ui_scale]);

  return {
    prefs,
    prefsChargees,
    updatePrefs,
    sidebarOrder: prefs.sidebar_order || null,
    sidebarItems: prefs.sidebar_items || null,
    sidebarAlphaSort: !!prefs.sidebar_alpha_sort,
    sidebarAlphaSortSections: prefs.sidebar_alpha_sort_sections ?? !!prefs.sidebar_alpha_sort,
    sidebarAlphaSortItems: prefs.sidebar_alpha_sort_items ?? !!prefs.sidebar_alpha_sort,
    customLabels: prefs.custom_labels || {},
    globalDefaults: _globalDefaults,
    uiScale: prefs.ui_scale || 1.0,
    getColumnOrder: (tableId) => (prefs.column_orders || {})[tableId] || null,
    setSidebarOrder,
    setSidebarItems,
    setColumnOrder,
    setCustomLabel,
    setUiScale,
    setSidebarAlphaSort,
    setSidebarAlphaSortSections,
    setSidebarAlphaSortItems,
    setVisite,
    saveAsGlobalDefault,
  };
}
